import React, { useEffect, useState } from "react";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LuLoader, LuLogOut, LuMonitor, LuSmartphone } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuth } from "../../context/AuthContext";
import useConfirmPopup from "../../context/useConfirmPopup";
import {
  fetchSessionsData,
  getSessions,
  getSessionsLoading,
} from "../../features/sessions/sessionsSlice";
import { DELETE } from "../../services/axios.service";
import {
  PIN_UNLOCKED_STORAGE_KEY,
  releaseUnlockPresence,
} from "../../static/pinPresence";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { SessionProps } from "../../types/session";

const ALL = -1;

const SessionManager = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmPopup = useConfirmPopup();
  const { setIsAuthenticated } = useAuth();

  const sessions = useAppSelector(getSessions);
  const loading = useAppSelector(getSessionsLoading);

  const [actionId, setActionId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchSessionsData());
  }, [dispatch]);

  const handleSignOut = async () => {
    await localStorage.removeItem("authToken");
    sessionStorage.removeItem(PIN_UNLOCKED_STORAGE_KEY);
    releaseUnlockPresence();
    await setIsAuthenticated(false);

    navigate(route.login);
  };

  const handleRevoke = async (session: SessionProps) => {
    const confirmed = await confirmPopup(
      "શું તમે ખાતરી કરો છો?",
      "આ સેશન લૉગ આઉટ થઇ જશે.",
      "લૉગ આઉટ કરો"
    );

    if (!confirmed) return;

    setActionId(session.id);

    await DELETE(`/v1/sessions/${session.id}`)
      .then(async (response: any) => {
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });

          if (session.isCurrent) {
            await handleSignOut();
            return;
          }

          dispatch(fetchSessionsData());
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      })
      .catch((error: any) => {
        toast.error(
          error?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      });

    setActionId(null);
  };

  const handleRevokeAll = async () => {
    const confirmed = await confirmPopup(
      "શું તમે ખાતરી કરો છો?",
      "બધા ઉપકરણો પરથી લૉગ આઉટ થઇ જશે.",
      "લૉગ આઉટ કરો"
    );

    if (!confirmed) return;

    setActionId(ALL);

    await DELETE("/v1/sessions")
      .then(async (response: any) => {
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });

          await handleSignOut();
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      })
      .catch((error: any) => {
        toast.error(
          error?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      });

    setActionId(null);
  };

  const renderIcon = (device: string | null) =>
    device === "mobile" ? (
      <LuSmartphone className="h-5 w-5 text-[#34699A]" />
    ) : (
      <LuMonitor className="h-5 w-5 text-[#34699A]" />
    );

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 p-6">
        <div>
          <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight">
            સક્રિય સત્રો
          </div>
          <div className="text-sm text-gray-600 mt-1.5">
            તમે જે ઉપકરણો પર લૉગ ઇન છો તે અહીં છે.
          </div>
        </div>

        {sessions.length > 0 && (
          <button
            type="button"
            onClick={handleRevokeAll}
            disabled={actionId !== null}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md h-10 px-4 py-2 text-sm bg-gradient-to-r from-[#991B1B] to-[#EF4444] hover:from-[#7F1D1D] hover:to-[#DC2626] text-white border-0 font-semibold transition-all duration-300 disabled:pointer-events-none disabled:opacity-70"
          >
            {actionId === ALL ? (
              <LuLoader className="h-4 w-4 animate-spin" />
            ) : (
              <LuLogOut className="h-4 w-4" />
            )}
            <span className="ml-2">બધેથી લૉગ આઉટ કરો</span>
          </button>
        )}
      </div>

      <div className="p-6 pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67] mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">લોડ થઈ રહ્યું છે...</p>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">કોઈ સક્રિય સત્ર મળ્યું નથી</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between gap-4 rounded-lg border border-gray-100 p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#34699A]/10 flex-shrink-0">
                    {renderIcon(session.device)}
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#113F67]">
                        {session.browser} • {session.os}
                      </span>
                      {session.isCurrent && (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                          આ ઉપકરણ
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-500">
                      {session.device}
                      {session.ipAddress ? ` • ${session.ipAddress}` : ""}
                    </div>

                    <div className="text-xs text-gray-400">
                      છેલ્લે સક્રિય:{" "}
                      {session.lastActiveAt
                        ? moment(session.lastActiveAt).format(
                            "DD MMMM, YYYY hh:mm A"
                          )
                        : "-"}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRevoke(session)}
                  disabled={actionId !== null}
                  title="લૉગ આઉટ"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md h-9 px-3 text-sm bg-gradient-to-r from-[#991B1B] to-[#EF4444] hover:from-[#7F1D1D] hover:to-[#DC2626] text-white border-0 transition-all duration-300 flex-shrink-0 disabled:pointer-events-none disabled:opacity-70"
                >
                  {actionId === session.id ? (
                    <LuLoader className="h-4 w-4 animate-spin" />
                  ) : (
                    <LuLogOut className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default SessionManager;
